// frontend/src/services/eventoService.js
import apiClient from './apiClient';

const API_URL = '/'; // Asegúrate de que la URL base sea correcta

const normalizarCanalesNotificacion = (canales) => {
    if (!canales) return null;

    if (Array.isArray(canales)) {
        const canalesValidos = canales.filter(c => c);
        return canalesValidos.length > 0 ? canalesValidos : null;
    }

    if (typeof canales === 'object') {
        const canalesDesdeObjeto = Object.entries(canales)
            .filter(([, activo]) => Boolean(activo))
            .map(([canal]) => canal);
        return canalesDesdeObjeto.length > 0 ? canalesDesdeObjeto : null;
    }

    return null;
};

const getEventos = (params = {}) => {
    return apiClient.get(`/eventos`, { params });
};

const getEventosSoloPublicos = () => {
    return apiClient.get(`/eventos`, { params: { soloPublicos: true } });
};

const getTiposEvento = () => {
    // Asumo que tienes un endpoint para obtener los tipos de evento.
    // Si no existe, deberás crearlo en tu backend.
    return apiClient.get(`/tipos-evento`);
};

const getEventosPorTramite = (idTramite) => {
    return apiClient.get(`/eventos`, {
        params: { idTramite }
    });
};

const getEventoById = (id) => {
    return apiClient.get(`/eventos/${id}`);
};

const crearEvento = async (eventoData, dispararEventoCalendario = true) => {
    try {
        // Validar datos básicos antes de enviar
        if (!eventoData) {
            throw new Error('Los datos del evento son requeridos');
        }
        
        if (!eventoData.fecha) {
            throw new Error('La fecha del evento es requerida');
        }
        
        if (!eventoData.tipoEvento) {
            throw new Error('El tipo de evento es requerido');
        }
        
        // Asegurarse de que los campos opcionales nulos no se envíen como strings vacíos
        const datosLimpios = { ...eventoData };
        if (datosLimpios.idTramite === '') {
            datosLimpios.idTramite = null;
        }
        // Enviar siempre visibilidad como String (PUBLICO, PRIVADO, POR_AREA)
        datosLimpios.visibilidad = eventoData.visibilidad || 'PUBLICO';
        
        // Acepta canales como array u objeto y los normaliza a lista
        datosLimpios.canalesNotificacion = normalizarCanalesNotificacion(datosLimpios.canalesNotificacion);
        
        const response = await apiClient.post(`/eventos`, datosLimpios);

        // Disparar evento personalizado para actualizar el calendario si se solicita
        // Por defecto, siempre se dispara para mantener la sincronización
        if (dispararEventoCalendario) {
            try {
                console.log('📅 EventoService: Evento creado - disparando actualización del calendario...');
                window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { 
                    detail: { timestamp: Date.now(), source: 'eventoService', eventoId: response.data?.idEvento } 
                }));
            } catch (eventError) {
                // Si falla el evento personalizado, no es crítico, solo loguear
                console.warn('⚠️ No se pudo disparar el evento personalizado para actualizar el calendario:', eventError);
            }
        }
        
        // El backend puede crear el evento exitosamente aunque las notificaciones fallen
        // Esto es esperado y no debe considerarse un error
        return response;
    } catch (error) {
        // Mejorar el mensaje de error para el usuario
        if (error.response) {
            // El servidor respondió con un código de error
            const mensajeError = error.response.data?.message || error.response.data || error.message;
            console.error('Error al crear evento:', mensajeError);
            throw new Error(`Error al crear evento: ${mensajeError}`);
        } else if (error.request) {
            // La petición se hizo pero no hubo respuesta
            console.error('Error de red al crear evento:', error.request);
            throw new Error('No se pudo conectar con el servidor. Por favor, verifique su conexión.');
        } else {
            // Algo más causó el error
            console.error('Error inesperado al crear evento:', error.message);
            throw error;
        }
    }
};

const modificarEvento = (idEvento, eventoData) => {
    const payload = { ...eventoData };
    if (payload.idTramite === '') {
        payload.idTramite = null;
    }
    payload.visibilidad = eventoData.visibilidad || 'PUBLICO';
    payload.canalesNotificacion = normalizarCanalesNotificacion(payload.canalesNotificacion);
    return apiClient.put(`/eventos/${idEvento}`, payload);
};

const eliminarEvento = (idEvento) => {
    return apiClient.delete(`/eventos/${idEvento}`);
};

const EventoService = {
    getEventos,
    getEventosSoloPublicos,
    getTiposEvento,
    crearEvento,
    modificarEvento,
    eliminarEvento,
    getEventosPorTramite,
    getEventoById,
};

export default EventoService;
